import styled from 'styled-components';
import PropTypes from 'prop-types';
import { isFunction } from 'lodash';

import { hexToRgba } from '../../helpers/utils/common';

import { COLORS, MobileSize } from '../../helpers/constants/styleguide';
import LoadingBars from '../LoadingBars';

/**
 * No rest spread: props not listed here are dropped rather than forwarded.
 *
 * @typedef {Object} ButtonProps
 * @property {React.ReactNode} [children]
 * @property {boolean} [disabled]
 * @property {boolean} [fakeDisabled]
 * @property {boolean} [fullWidth]
 * @property {boolean} [borderless]
 * @property {((node: HTMLElement | null) => void) | null} [innerRef]
 * @property {boolean} [isLoading]
 * @property {React.MouseEventHandler<HTMLButtonElement>} [onClick]
 * @property {boolean} [secondary]
 * @property {'button' | 'submit' | 'reset'} [type]
 * @property {number} [width]
 * @property {string} [className]
 * @property {string} [dataTestId]
 * @property {((event: string, properties?: object) => void) | null} [track]
 * @property {string} [kind] Used for tracking
 * @property {string} [backgroundColor]
 * @property {string} [secondaryColor]
 * @property {string} [textColor]
 * @property {string | number} [textSize]
 */

/** @type {React.FC<ButtonProps>} */
const Button = ({
  children = '',
  disabled = false,
  fakeDisabled = false,
  fullWidth = false,
  borderless = false,
  innerRef = null,
  isLoading = false,
  onClick = () => {},
  secondary = false,
  type = 'button',
  width = 360,
  className,
  dataTestId = '',
  track,
  kind,
  backgroundColor = COLORS.azure,
  secondaryColor,
  textColor,
  textSize,
}) => {
  const clickHandler = e => {
    if (isFunction(track)) {
      track('User clicked button', {
        button: kind || children,
      });
    }
    if (isFunction(onClick)) {
      onClick(e);
    }
  };

  const buttonTextColor =
    textColor || getTextColor(backgroundColor, disabled, secondary);

  return (
    <StyledButton
      className={className}
      ref={innerRef}
      type={type}
      width={width}
      fullWidth={fullWidth}
      borderless={borderless}
      disabled={disabled || isLoading}
      fakeDisabled={fakeDisabled}
      onClick={disabled || isLoading ? null : clickHandler}
      secondary={secondary}
      data-testid={dataTestId || `button-${type}-${kind || children}`}
      data-ph-capture-attribute-button-intent={
        secondary ? 'secondary' : 'primary'
      }
      data-ph-capture-attribute-button-type={type}
      data-ph-capture-attribute-track-id={kind}
      backgroundColor={backgroundColor}
      secondaryColor={secondaryColor}
    >
      {isLoading ? (
        <LoadingBars color={secondary ? COLORS.medGrey : null} />
      ) : (
        <Text
          textColor={buttonTextColor}
          secondary={secondary}
          disabled={disabled || fakeDisabled}
          textSize={textSize}
        >
          {children}
        </Text>
      )}
    </StyledButton>
  );
};

Button.propTypes = {
  /** Button text */
  children: PropTypes.node.isRequired,
  /** Click handler */
  onClick: PropTypes.func,
  /** HTML type attribute */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  disabled: PropTypes.bool,
  fakeDisabled: PropTypes.bool,
  /** Determines if the button should be full width on non-mobile widths */
  fullWidth: PropTypes.bool,
  /** Allows you to remove border to create a link-styled button */
  borderless: PropTypes.bool,
  isLoading: PropTypes.bool,
  secondary: PropTypes.bool,
  width: PropTypes.number,
  innerRef: PropTypes.func,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  /** Track function from analytics */
  track: PropTypes.func,
  /** String used for tracking */
  kind: PropTypes.string,
  backgroundColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  textColor: PropTypes.string,
  textSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const getColor = (propColor, disabled, secondary, secondaryColor) => {
  if (secondary) {
    return secondaryColor || COLORS.white;
  }
  if (disabled) {
    return COLORS.medGrey;
  }
  return propColor;
};

const getTextColor = (textColor, disabled, secondary) => {
  if (!secondary) {
    return COLORS.white;
  }
  return disabled ? COLORS.medGrey : textColor;
};

const StyledButton = styled.button.attrs(props => ({
  className: (props.disabled || props.fakeDisabled) && 'disabled',
}))`
  border: ${props =>
    props.borderless
      ? '0'
      : `1px solid ${getColor(
          props.backgroundColor,
          props.disabled || props.fakeDisabled,
          false,
        )}`};
  width: ${props => (props.fullWidth ? '100%' : `${props.width}px`)};
  min-width: 100px;
  padding: 14px 8px;
  user-select: none;

  @media (max-width: ${MobileSize}) {
    width: 100%;
  }

  background-color: ${({
    backgroundColor,
    disabled,
    fakeDisabled,
    secondary,
    secondaryColor,
  }) => {
    return getColor(
      backgroundColor,
      disabled || fakeDisabled,
      secondary,
      secondaryColor,
    );
  }};

  border-radius: 3px;
  cursor: ${props =>
    props.disabled || props.fakeDisabled ? 'default' : 'pointer'};

  &:hover {
    opacity: ${props => !props.disabled && !props.fakeDisabled && '0.8'};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    ${({ disabled, fakeDisabled }) => {
      if (!disabled && !fakeDisabled) {
        return `box-shadow: 0 0 0 2.5px ${COLORS.white}, 0px 0px 0px 4px ${COLORS.azure};`;
      }
      return ``;
    }}
  }

  &:active {
    ${({ backgroundColor, disabled, fakeDisabled, secondary }) => {
      if (disabled || fakeDisabled) {
        return `background-color: ${getColor(
          backgroundColor,
          disabled || fakeDisabled,
          secondary,
        )};`;
      }

      const color = getColor(backgroundColor, false, false);
      return secondary
        ? `background-color: ${hexToRgba(color, 0.2)};`
        : `border: 1px solid ${hexToRgba(color, 0.1)};
        background-color: ${hexToRgba(color, 0.7)};`;
    }}
  }
`;

// A span, because PostHog reads a button's text only from its own text and
// nested spans.
const Text = styled.span`
  display: block;
  font-size: ${({ textSize }) => textSize ?? '16px'};
  font-weight: 600;
  width: 100%;
  color: ${({ textColor }) => textColor};
  opacity: ${props => props.disabled && '0.7'};
`;

export default Button;
